import { useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink, Trash2 } from "lucide-react";

/** Tam ekran gorsel onizleme — galeri icinde ileri/geri gecisli.
 *
 *  Uretilen gorselleri tek tek yeni sekmede acmak akisi bozuyordu; burada
 *  kucuk resme tiklanir, ok tuslari (veya yandaki dugmeler) ile seri
 *  gezilir, Esc ile cikilir. Alttaki serit hangi karede olundugunu gosterir
 *  ve dogrudan atlamaya yarar. */
export interface LightboxCaption {
  text?: string | null;
  /** Gorselin kaynagi (rakip liste adresi) — yeni sekmede acilir */
  url?: string | null;
  /** Yaninda gosterilecek REFERANS gorseller (konsept sahnesi, urun fotografi…) */
  refs?: Array<{ url: string; label: string }>;
}

export function ImageLightbox({ urls, index, onClose, onIndex, captions, onDelete }: {
  urls: string[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
  /** urls ile AYNI sirada: baslik ve kaynak linki */
  captions?: Array<LightboxCaption | null | undefined>;
  /** Verilirse cop kutusu gorunur. Hatali uretimler cogu zaman TAM EKRANDA
   *  fark ediliyor; silmek icin once kapatmak gerekmesin. */
  onDelete?: (i: number) => void;
}) {
  const caption = captions?.[index];
  const step = (d: number) => onIndex((index + d + urls.length) % urls.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndex((index + 1) % urls.length);
      else if (e.key === "ArrowLeft") onIndex((index - 1 + urls.length) % urls.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, urls.length, onClose, onIndex]);

  if (!urls[index]) return null;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-black/85 p-4" onClick={onClose}>
      <div className="absolute right-4 top-4 flex items-center gap-2">
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(index); }}
            title="Bu görseli sil"
            className="rounded-full bg-white/15 p-2 text-red-300 hover:bg-red-500/30 hover:text-red-100"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
        <button onClick={onClose} className="rounded-full bg-white/15 p-2 text-white hover:bg-white/25">
          <X className="h-5 w-5" />
        </button>
      </div>

      {(caption?.text || caption?.url) && (
        <div className="mb-2 flex max-w-[92vw] items-center gap-3 text-xs text-white/90" onClick={(e) => e.stopPropagation()}>
          {caption.text && <span className="truncate">{caption.text}</span>}
          {caption.url && (
            <a href={caption.url} target="_blank" rel="noreferrer"
              className="flex shrink-0 items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 hover:bg-white/25">
              <ExternalLink className="h-3.5 w-3.5" /> Kaynak ürün
            </a>
          )}
        </div>
      )}

      {caption?.refs?.length ? (
        // Uretilen gorsel + yaninda hangi referanslarla uretildigi
        <div className="flex max-h-[82vh] max-w-[96vw] items-start gap-3" onClick={(e) => e.stopPropagation()}>
          <img src={urls[index]} alt="" className="max-h-[82vh] max-w-[70vw] object-contain" />
          <div className="flex max-h-[82vh] w-[22vw] min-w-[180px] flex-col gap-2 overflow-y-auto">
            {caption.refs.map((r) => (
              <div key={r.url} className="rounded-lg bg-white/10 p-1.5">
                <p className="mb-1 text-[11px] font-medium text-white/80">{r.label}</p>
                <img src={r.url} alt="" className="w-full rounded object-contain" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <img
          src={urls[index]}
          alt=""
          onClick={(e) => e.stopPropagation()}
          className="max-h-[82vh] max-w-[92vw] object-contain"
        />
      )}

      {urls.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white hover:bg-white/25"
            title="Önceki (←)"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white hover:bg-white/25"
            title="Sonraki (→)"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <div className="mt-3 flex max-w-[92vw] items-center gap-2 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
            {urls.map((u, i) => (
              <img
                key={u + i}
                src={u}
                alt=""
                loading="lazy"
                onClick={() => onIndex(i)}
                className={`h-12 w-12 shrink-0 cursor-pointer rounded object-cover ${
                  i === index ? "ring-2 ring-white" : "opacity-50 hover:opacity-90"
                }`}
              />
            ))}
          </div>
          <span className="mt-2 text-xs text-white/80">{index + 1} / {urls.length}</span>
        </>
      )}
    </div>
  );
}

export default ImageLightbox;
